import { CardPosition, EnginePhase, ResponseMode, Seat } from './state';

// Intentions sent by a player (or the AI) to the authoritative engine.
// The engine validates that the action is legal for the acting seat/phase before applying it.

export enum ActionType {
  NORMAL_SUMMON = 'NORMAL_SUMMON',
  SET_MONSTER = 'SET_MONSTER',
  TRIBUTE_SUMMON = 'TRIBUTE_SUMMON',
  CHANGE_POSITION = 'CHANGE_POSITION',
  FLIP_SUMMON = 'FLIP_SUMMON',
  ACTIVATE_SPELL_FROM_HAND = 'ACTIVATE_SPELL_FROM_HAND',
  SET_SPELL_TRAP = 'SET_SPELL_TRAP',
  ACTIVATE_SET_SPELL = 'ACTIVATE_SET_SPELL',
  ACTIVATE_TRAP = 'ACTIVATE_TRAP',
  DECLARE_ATTACK = 'DECLARE_ATTACK',
  DIRECT_ATTACK = 'DIRECT_ATTACK',
  ADVANCE_PHASE = 'ADVANCE_PHASE',
  SELECT_TARGETS = 'SELECT_TARGETS',
  RESPOND_PRIORITY = 'RESPOND_PRIORITY',
  RESOLVE_FLIP_TARGET = 'RESOLVE_FLIP_TARGET',
  DISCARD = 'DISCARD',
  SURRENDER = 'SURRENDER',
  /** Server-only: the seat left the room on purpose. */
  FORFEIT = 'FORFEIT',
  /** Server-only: the acting seat's chess clock hit zero. */
  TIME_OUT = 'TIME_OUT',
  /** Client-only preference: how eager trap-response prompts should be. */
  SET_RESPONSE_MODE = 'SET_RESPONSE_MODE',
  /** Courtesy, not a play: reorder this seat's hand. Legal in any window. */
  SHUFFLE_HAND = 'SHUFFLE_HAND',
  /** Time Wizard or Barrel Dragon, face-up, your Main Phase. */
  ACTIVATE_MONSTER_EFFECT = 'ACTIVATE_MONSTER_EFFECT',
  /**
   * Gate Guardian: Tribute Sanga, Kazejin and Suijin, then Special Summon
   * from the hand. Does not use the Normal Summon.
   */
  SPECIAL_SUMMON_PROC = 'SPECIAL_SUMMON_PROC',
  /** Cocoon of Evolution: equip this monster from the hand onto Petit Moth. */
  EQUIP_FROM_HAND = 'EQUIP_FROM_HAND',
  /** Answer a pending Heads/Tails call. */
  CALL_COIN = 'CALL_COIN',
  /** Blast Juggler or Mushroom Man #2: yes or no. */
  ANSWER_CHOICE = 'ANSWER_CHOICE',
  /**
   * Order this seat's simultaneous mandatory effects.
   * The first uid is Chain Link 1 and resolves last.
   */
  ORDER_CHAIN = 'ORDER_CHAIN',
}

export interface NormalSummonAction {
  type: ActionType.NORMAL_SUMMON;
  cardUid: string;
  /** target monster zone index. */
  zoneIndex: number;
}

export interface SetMonsterAction {
  type: ActionType.SET_MONSTER;
  cardUid: string;
  zoneIndex: number;
}

export interface TributeSummonAction {
  type: ActionType.TRIBUTE_SUMMON;
  cardUid: string;
  zoneIndex: number;
  tributeUids: string[];
  /** ATK = face-up attack, DEF = face-up defense, undefined => prompt; SET handled separately. */
  position: CardPosition;
  faceDown: boolean;
}

export interface ChangePositionAction {
  type: ActionType.CHANGE_POSITION;
  cardUid: string;
}

export interface FlipSummonAction {
  type: ActionType.FLIP_SUMMON;
  cardUid: string;
  /** Flip-effect target the AI already chose. Humans pick in the window. */
  targetUid?: string;
}

export interface ActivateSpellFromHandAction {
  type: ActionType.ACTIVATE_SPELL_FROM_HAND;
  cardUid: string;
  targetUids?: string[];
  /** Face-up ATK or DEF for a Special Summon this activation performs (Reborn, Fusion). */
  position?: CardPosition;
}

export interface SetSpellTrapAction {
  type: ActionType.SET_SPELL_TRAP;
  cardUid: string;
  zoneIndex: number;
}

export interface ActivateSetSpellAction {
  type: ActionType.ACTIVATE_SET_SPELL;
  cardUid: string;
  targetUids?: string[];
  /** Face-up ATK or DEF for a Special Summon this activation performs (Reborn, Fusion). */
  position?: CardPosition;
}

export interface ActivateTrapAction {
  type: ActionType.ACTIVATE_TRAP;
  cardUid: string;
  targetUids?: string[];
}

export interface DeclareAttackAction {
  type: ActionType.DECLARE_ATTACK;
  attackerUid: string;
  targetUid: string;
}

export interface DirectAttackAction {
  type: ActionType.DIRECT_ATTACK;
  attackerUid: string;
}

export interface AdvancePhaseAction {
  type: ActionType.ADVANCE_PHASE;
  /**
   * Skip optional later phases and land here. Only End Phase is allowed
   * (M1 → EP skips Battle and Main 2; BP → EP skips Main 2).
   */
  to?: EnginePhase;
}

export interface SelectTargetsAction {
  type: ActionType.SELECT_TARGETS;
  /** correlation id matching a pending REQUEST_TARGETS event. */
  requestId: string;
  targetUids: string[];
  /**
   * The chooser has nothing to click. The engine honors this only when the
   * window's candidate list is already empty; a legal target must still be
   * chosen.
   */
  abandon?: boolean;
  /** Face-up ATK or DEF for a Special Summon this activation performs (Reborn). */
  position?: CardPosition;
  /**
   * One face-up position per uid, in the same order as `targetUids`.
   * Used when one activation Special Summons more than one monster
   * (The Flute of Summoning Dragon).
   */
  positions?: CardPosition[];
}

export interface RespondPriorityAction {
  type: ActionType.RESPOND_PRIORITY;
  requestId: string;
  /** uid of a card to activate in response, or null to pass. */
  cardUid: string | null;
  /**
   * Targets for the activated card, when it needs more than the monster that opened
   * the window (Two-Pronged Attack also destroys two of the responder's monsters).
   * Omitted, the trigger itself is used as the single target.
   */
  targetUids?: string[];
  /** Sanga, Kazejin, or Suijin: which stat gains 1000 until the end of damage. */
  boost?: 'atk' | 'def';
}

export interface ResolveFlipTargetAction {
  type: ActionType.RESOLVE_FLIP_TARGET;
  requestId: string;
  /** uid of the chosen target card. */
  targetUid: string;
}

export interface DiscardAction {
  type: ActionType.DISCARD;
  /** uids of hand cards to discard (to satisfy the hand-size limit). */
  cardUids: string[];
}

export interface SurrenderAction {
  type: ActionType.SURRENDER;
}

export interface ForfeitAction {
  type: ActionType.FORFEIT;
}

export interface TimeOutAction {
  type: ActionType.TIME_OUT;
}

export interface SetResponseModeAction {
  type: ActionType.SET_RESPONSE_MODE;
  mode: ResponseMode;
}

export interface ShuffleHandAction {
  type: ActionType.SHUFFLE_HAND;
}

export interface ActivateMonsterEffectAction {
  type: ActionType.ACTIVATE_MONSTER_EFFECT;
  cardUid: string;
}

export interface SpecialSummonProcAction {
  type: ActionType.SPECIAL_SUMMON_PROC;
  cardUid: string;
  position: CardPosition;
  tributeUids: string[];
}

export interface EquipFromHandAction {
  type: ActionType.EQUIP_FROM_HAND;
  cardUid: string;
  targetUid: string;
}

export interface CallCoinAction {
  type: ActionType.CALL_COIN;
  requestId: string;
  call: 'heads' | 'tails';
}

export interface AnswerChoiceAction {
  type: ActionType.ANSWER_CHOICE;
  requestId: string;
  accept: boolean;
}

export interface OrderChainAction {
  type: ActionType.ORDER_CHAIN;
  requestId: string;
  /** First uid is Chain Link 1 and resolves last. Every pending card, once. */
  cardUids: string[];
}

export type PlayerAction =
  | NormalSummonAction
  | SetMonsterAction
  | TributeSummonAction
  | ChangePositionAction
  | FlipSummonAction
  | ActivateSpellFromHandAction
  | SetSpellTrapAction
  | ActivateSetSpellAction
  | ActivateTrapAction
  | DeclareAttackAction
  | DirectAttackAction
  | AdvancePhaseAction
  | SelectTargetsAction
  | RespondPriorityAction
  | ResolveFlipTargetAction
  | DiscardAction
  | SurrenderAction
  | ForfeitAction
  | TimeOutAction
  | SetResponseModeAction
  | ShuffleHandAction
  | ActivateMonsterEffectAction
  | SpecialSummonProcAction
  | EquipFromHandAction
  | CallCoinAction
  | AnswerChoiceAction
  | OrderChainAction;

/** An action plus the seat that issued it (server attaches the authenticated seat). */
export interface SeatedAction {
  seat: Seat;
  action: PlayerAction;
}
